import {
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Statistic,
  Table,
  Tag,
  Typography,
  theme as antdTheme,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { reportsApi, type FeeTypeYieldRow } from '@/api/reports';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';
import { useSchoolTheme } from '@/themes/use-theme';
import { useAuthStore } from '@/store/auth';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function FeeTypeYieldReportPage() {
  const { token } = antdTheme.useToken();
  const { chartColors } = useSchoolTheme();
  const academicYearId = useAuthStore((s) => s.academicYearId);

  const { data, isFetching } = useQuery({
    queryKey: ['reports', 'fee-type-yield', academicYearId],
    queryFn: () => reportsApi.feeTypeYield(academicYearId ? { academicYearId } : {}),
  });

  const chart = (data?.rows ?? []).map((r) => ({
    name: r.feeTypeName,
    billed: Number(r.billed),
    collected: Number(r.collected),
  }));

  const tooltipStyle = {
    background: token.colorBgElevated,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 8,
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Reports / Finance"
        title="Fee-type yield"
        description="Budgeted (fee structures × current headcount) vs what's actually been billed and collected, per fee type. Expected is a per-month run-rate for monthly fees."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/fee-type-yield.xlsx',
              params: academicYearId ? { academicYearId } : {},
              fileName: 'fee-type-yield.xlsx',
              disabled: !data,
            }]}
          />
        }
      />

      {data ? (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card><Statistic title="Expected (monthly run-rate)" value={PKR(data.totalExpected)} prefix="₨" /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Total billed" value={PKR(data.totalBilled)} prefix="₨" /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Total collected" value={PKR(data.totalCollected)} prefix="₨" styles={{ content: { color: token.colorSuccess } }} /></Card>
            </Col>
            <Col xs={12} md={6}>
              <Card><Statistic title="Outstanding" value={PKR(data.totalOutstanding)} prefix="₨" styles={{ content: { color: token.colorError } }} /></Card>
            </Col>
          </Row>

          <Card title={<Typography.Text strong>Billed vs collected by fee type</Typography.Text>}>
            {chart.length === 0 ? (
              <Empty description="No fee activity in this academic year yet" />
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(240, chart.length * 52)}>
                <BarChart data={chart} layout="vertical" margin={{ left: 12, right: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} horizontal={false} />
                  <XAxis type="number" stroke={token.colorTextSecondary} fontSize={12} tickFormatter={(v) => PKR(v as number)} />
                  <YAxis type="category" dataKey="name" stroke={token.colorTextSecondary} fontSize={12} width={130} />
                  <Tooltip formatter={(v: number, n) => [`₨ ${PKR(v)}`, n as string]} contentStyle={tooltipStyle} cursor={{ fill: token.colorFillTertiary }} />
                  <Legend />
                  <Bar dataKey="billed" name="Billed" fill={chartColors[2 % chartColors.length]} radius={[0, 4, 4, 0]} />
                  <Bar dataKey="collected" name="Collected" fill={chartColors[3 % chartColors.length]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card title="Per fee type" styles={{ body: { padding: 0 } }}>
            {data.rows.length === 0 ? (
              <Empty style={{ padding: 24 }} description="No data" />
            ) : (
              <Table<FeeTypeYieldRow>
                rowKey="feeTypeId"
                size="small"
                pagination={false}
                loading={isFetching}
                dataSource={data.rows}
                scroll={{ x: 900 }}
                columns={[
                  { title: 'Fee type', dataIndex: 'feeTypeName' },
                  { title: 'Frequency', dataIndex: 'frequency', width: 110, render: (f: string | null) => (f ? <Tag>{f}</Tag> : '—') },
                  { title: 'Expected', dataIndex: 'expected', width: 130, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                  { title: 'Billed', dataIndex: 'billed', width: 130, align: 'right', render: (a: string) => `₨ ${PKR(a)}` },
                  { title: 'Collected', dataIndex: 'collected', width: 130, align: 'right', render: (a: string) => <span style={{ color: token.colorSuccess }}>₨ {PKR(a)}</span> },
                  { title: 'Outstanding', dataIndex: 'outstanding', width: 130, align: 'right', render: (a: string) => <span style={{ color: Number(a) > 0 ? token.colorError : undefined }}>₨ {PKR(a)}</span> },
                  {
                    title: 'Collection %',
                    dataIndex: 'collectionPct',
                    width: 160,
                    render: (p: string) => (
                      <Progress
                        percent={Math.min(100, Number(p))}
                        size="small"
                        format={() => `${PKR(p)}%`}
                        status={Number(p) >= 90 ? 'success' : Number(p) >= 60 ? 'active' : 'exception'}
                      />
                    ),
                  },
                ]}
              />
            )}
          </Card>
        </>
      ) : (
        <Empty />
      )}
    </div>
  );
}
